import React, { useState, useEffect } from 'react';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home, ArrowUpCircleIcon, WalletIcon, Clock, Calendar, TrendingDown, Zap, Award } from "lucide-react";
import {
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon
} from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const TopUpLoan = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Key Benefits state
  const [keyBenefits, setKeyBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState(null);
  const [benefitsLoading, setBenefitsLoading] = useState(true);

  // Eligibility Criteria state
  const [eligibilityCriteria, setEligibilityCriteria] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  // Documents Required state
  const [documentsData, setDocumentsData] = useState([]);
  const [documentsSettings, setDocumentsSettings] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Interest Rates state
  const [interestRatesCards, setInterestRatesCards] = useState([]);
  const [interestRatesSettings, setInterestRatesSettings] = useState(null);
  const [interestRatesLoading, setInterestRatesLoading] = useState(true);

  // FAQs state
  const [faqsData, setFaqsData] = useState([]);
  const [faqsSettings, setFaqsSettings] = useState(null);
  const [faqsLoading, setFaqsLoading] = useState(true);

  // CTA Banner state
  const [ctaBannerSettings, setCtaBannerSettings] = useState(null);
  const [ctaBannerLoading, setCtaBannerLoading] = useState(true);

  // Fallback banner data
  const fallbackBanner = {
    title: 'Need Extra Funds? Get an Aham Top-Up Loan',
    subtitle: 'Quick approval and minimal paperwork—ideal for renovation, education, business needs, or personal expenses.',
    image_url: 'img/loan-banners/Top-up-loan.webp',
    highlights: [],
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact'
  };


  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Key Benefits Data
  const benefits = [
    {
      icon: ArrowUpCircleIcon,
      title: "Additional Funding Without New Loan",
      description: "Get extra funds on your existing Aham home loan without taking a separate loan."
    },
    {
      icon: BanknotesIcon,
      title: "Loan up to ₹10 Lakhs – ₹25 Lakhs",
      description: "High-value top-up loan depending on your repayment track and property value."
    },
    {
      icon: ClockIcon,
      title: "Flexible Tenure up to 15 Years",
      description: "Extend your repayment period or keep it shorter based on your preference."
    },
    {
      icon: ShieldCheckIcon,
      title: "Quick & Hassle-Free Processing",
      description: "Fast approval based on repayment history with minimal additional documentation."
    },
    {
      icon: WalletIcon,
      title: "Competitive Interest Rates",
      description: "Top-up loan offered at attractive rates lower than typical personal loans."
    },
    {
      icon: HomeIcon,
      title: "Multi-Purpose Usage",
      description: "Use funds for renovation, education, medical expenses, furniture, business needs, or personal requirements."
    }
  ];


  // Eligibility Criteria
  const eligibility = [
    "An active home loan with Aham Housing Finance",
    "Minimum 12 months of clear repayment history",
    "Good repayment track record with no major overdue",
    "Sufficient repayment capacity based on FOIR norms (typically up to 50%)",
    "Property with adequate value to support additional funding",
    "Top-up amount assessed based on outstanding loan, income, and LTV norms"
  ];

  // Matching icons & colors for each item
  const eligibilityIconMap = {
    HomeIcon: Home,
    UserIcon: User,
    CheckCircleIcon: CheckCircle,
    BriefcaseIcon: Briefcase,
    ClockIcon: Clock,
    StarIcon: Star,
    ShieldCheckIcon: Shield,
    DocumentTextIcon: FileText
  };

  const icons = [
    { icon: User, from: "from-blue-500", to: "to-cyan-400" },
    { icon: Globe, from: "from-green-500", to: "to-lime-400" },
    { icon: Briefcase, from: "from-pink-500", to: "to-rose-400" },
    { icon: DollarSign, from: "from-orange-500", to: "to-amber-400" },
    { icon: FileText, from: "from-purple-500", to: "to-indigo-400" },
    { icon: Shield, from: "from-teal-500", to: "to-emerald-400" },
    { icon: Star, from: "from-yellow-500", to: "to-orange-400" },
    { icon: Ruler, from: "from-red-500", to: "to-orange-400" },
  ];

  // Documents Required
  const documents = {
    common: [
      "Top-up loan application form with photographs",
      "Identity Proof – Aadhaar, PAN, Passport, Driving License",
      "Address Proof – Aadhaar, Passport, Utility Bills, Rent Agreement",
      "Updated bank statements (Last 6 months)"
    ],

    salaried: [
      "Last 3 months salary slips",
      "Latest Form 16 or ITR",
      "Salary credit bank statements",
      'Employee ID Card'
    ],

    selfEmployed: [
      "Business registration documents – GST, TIN, Trade License",
      "Audited financials / ITR for last 2–3 years",
      "Business account statements (12 months)",
      "OCI/PIO Card (if applicable)"
    ],

    nri: [
      "Valid passport & visa",
      "Overseas employment contract and income proof",
      "NRE/NRO bank statements (last 6 months)",
      "Power of Attorney if applicable"
    ],

    property: [
      "Existing home loan details",
      "Outstanding loan statement",
      "Updated property valuation report (if required)",
      "No objection / declaration forms"
    ]
  };


   // Ilaiyaboopathi R
  // FAQs
  const faqs = [
    {
      question: "What is a Top-Up Loan?",
      answer:
        "A Top-Up Loan allows existing Aham Housing Finance customers to borrow additional funds over their current home loan without taking a separate loan."
    },
    {
      question: "How much top-up amount can I get?",
      answer:
        "You may get between ₹10 lakhs and ₹25 lakhs depending on your eligibility, repayment track, and property valuation."
    },
    {
      question: "What can a top-up loan be used for?",
      answer:
        "Top-up loans can be used for home renovation, business needs, medical expenses, education, or any personal financial requirement."
    },
    {
      question: "Is income proof required for a top-up loan?",
      answer:
        "Income documents may be needed depending on the requested top-up amount and repayment capacity."
    },
    {
      question: "Does the interest rate differ from home loan rate?",
      answer:
        "Top-up loans generally have slightly higher rates than home loans but are much cheaper than personal loans."
    },
    {
      question: "What is the repayment tenure for top-up loans?",
      answer:
        "Tenure can go up to 15 years or match your existing home loan tenure, whichever is earlier."
    },
    {
      question: "How quickly can a top-up loan be processed?",
      answer:
        "With a good repayment record, Aham can process top-up loans quickly with minimal documentation."
    }
  ];


  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  // Fetch banner data
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        console.log('Fetching banner for top_up_loan...');
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await axios.get(`/api/admin/banners/page/top_up_loan?t=${timestamp}`);
        console.log('Banner response:', response.data);

        const banners = response.data.data || [];

        if (response.data.success && banners.length > 0) {
          console.log('Setting banner data:', banners[0]);
          setBanner(banners[0]);
        } else {
          console.log('No banner data found, using fallback');
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        console.error('Error details:', error.response?.data || error.message);
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Fetch overview data
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        const response = await axios.get('/api/admin/loan-overview/top_up_loan');
        if (response.data.success && response.data.data) {
          setOverview(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
        // Use fallback - keep overview as null to use static content
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, []);

  // Icon mapping for key benefits
  const iconMap = {
    Clock, Calendar, TrendingDown, Zap, FileText, Award,
    Shield, Home, DollarSign, CheckCircle, Star
  };

  // Fetch key benefits
  useEffect(() => {
    const fetchKeyBenefits = async () => {
      try {
        setBenefitsLoading(true);
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/top_up_loan/active'),
          axios.get('/api/admin/key-benefits/settings/top_up_loan')
        ]);

        if (benefitsRes.data.success && benefitsRes.data.data) {
          setKeyBenefits(benefitsRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching key benefits:', error);
        // Use fallback - keep as empty to show static content
      } finally {
        setBenefitsLoading(false);
      }
    };

    fetchKeyBenefits();
  }, []);

  // Fetch eligibility criteria
  useEffect(() => {
    const fetchEligibilityCriteria = async () => {
      try {
        setEligibilityLoading(true);
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/eligibility/top_up_loan/active'),
          axios.get('/api/admin/eligibility/settings/top_up_loan')
        ]);

        if (criteriaRes.data.success && criteriaRes.data.data) {
          setEligibilityCriteria(criteriaRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setEligibilitySettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching eligibility criteria:', error);
        // Use fallback - keep as empty to show static content
      } finally {
        setEligibilityLoading(false);
      }
    };

    fetchEligibilityCriteria();
  }, []);

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setDocumentsLoading(true);
        const [documentsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/documents/top_up_loan/active'),
          axios.get('/api/admin/documents/settings/top_up_loan')
        ]);

        if (documentsRes.data.success && documentsRes.data.data) {
          setDocumentsData(documentsRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setDocumentsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        // Use fallback - keep as empty to show static content
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Fetch interest rates
  useEffect(() => {
    const fetchInterestRates = async () => {
      try {
        setInterestRatesLoading(true);
        const [cardsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/interest-rates/top_up_loan/active'),
          axios.get('/api/admin/interest-rates/settings/top_up_loan')
        ]);

        if (cardsRes.data.success && cardsRes.data.data) {
          setInterestRatesCards(cardsRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setInterestRatesSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates:', error);
      } finally {
        setInterestRatesLoading(false);
      }
    };

    fetchInterestRates();
  }, []);

  // Fetch FAQs
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setFaqsLoading(true);
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/faqs/top_up_loan/active'),
          axios.get('/api/admin/faqs/settings/top_up_loan')
        ]);

        if (faqsRes.data.success && faqsRes.data.data) {
          setFaqsData(faqsRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setFaqsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setFaqsLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  // Fetch CTA Banner
  useEffect(() => {
    const fetchCTABanner = async () => {
      try {
        setCtaBannerLoading(true);
        const response = await axios.get('/api/admin/cta-banner/settings/top_up_loan');

        if (response.data.success && response.data.data) {
          setCtaBannerSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner:', error);
      } finally {
        setCtaBannerLoading(false);
      }
    };

    fetchCTABanner();
  }, []);


  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };
            
              // Schema for SEO
  const topUpSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/top-up-loan-for-home-loan/#webpage",
        "url": "https://ahamhfc.com/top-up-loan-for-home-loan",
        "name": "Top-Up Loan for Home Loan - Aham Housing Finance",
        "description": "Explore top-up loan options from Aham Housing Finance for existing home loan customers who need additional funding with a simple and supportive process.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/top-up-loan-for-home-loan/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/top-up-loan-for-home-loan/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Composite-Loan-Overview-1768396922051-200171860.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/top-up-loan-for-home-loan/#loan",
        "name": "Top-Up Loan",
        "alternateName": "Top-Up Loan for Home Loan",
        "url": "https://ahamhfc.com/top-up-loan-for-home-loan",
        "image": {
          "@id": "https://ahamhfc.com/top-up-loan-for-home-loan/#primaryimage"
        },
        "description": "Top-up funding offered to existing home loan customers who have maintained a healthy repayment track and need additional finance.",
        "category": "Housing Finance",
        "loanType": "Top-Up Loan",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Existing home loan customers with healthy repayment track"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/top-up-loan-for-home-loan/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Top-Up Loan",
            "item": "https://ahamhfc.com/top-up-loan-for-home-loan"
          }
        ]
      }
    ]
  };




  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="Top-Up Loan for Home Loan: Attractive Rates | Aham Finance"
        description="Apply for a top-up home loan at attractive interest rates. Check eligibility, calculate EMIs, and enjoy tax benefits. Get additional funds with Aham today!"
        keywords="Aham top-up loan, Aham Housing Finance top-up loan, top-up loan, home loan top-up, top-up on existing home loan, additional loan on home loan, existing customer top-up loan, top-up funding Aham, housing loan top-up, mortgage top-up loan, extra funds on home loan, affordable top-up loan, low documentation top-up loan, no income proof top-up loan, self employed top-up loan, flexible top-up finance, home loan enhancement, top-up loan for existing borrowers, Aham additional funding, Aham Housing Finance extra loan"
        canonical="https://ahamhfc.com/top-up-loan-for-home-loan"
        ogTitle="Top-Up Loan for Home Loan: Attractive Rates | Aham Finance"
        ogDescription="Apply for a top-up home loan at attractive interest rates. Check eligibility, calculate EMIs, and enjoy tax benefits. Get additional funds with Aham today!"
       schema={topUpSchema}
           />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${banner?.image_url || fallbackBanner.image_url})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
             

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || fallbackBanner.title}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || fallbackBanner.subtitle}
                </p>

                {/* Highlights */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-4 mb-8">
                    {banner.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-white text-xs md:text-sm font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a href={banner?.primary_button_link || "https://aham.mbwhost.in/contact"} className="w-full sm:w-auto text-left">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                    >
                      {banner?.primary_button_text || "Apply Now"}
                    </motion.button>
                  </a>

                  {banner?.secondary_button_text && (
                    <a href={banner.secondary_button_link || "#"} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>
                
                   {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Top-up Loan</span>
                </motion.div>
                
              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}
      <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overview?.title || 'Top-Up Loan'}
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                {overview?.description ? (
                  <div dangerouslySetInnerHTML={{ __html: overview.description }} />
                ) : (
                  <>
                    <p>
                      Need extra funds without the hassle of applying for a new loan? Aham Housing Finance offers
                      <strong> Top-Up Loans </strong>
                      exclusively for existing home loan customers who have maintained a healthy repayment track.
                    </p>

                    <p>
                      Whether you want to renovate your home, manage medical expenses, fund your business, or cover education costs, a top-up loan gives you additional financial flexibility with minimal documentation.
                    </p>
                  </>
                )}

                <ul className="space-y-3">
                  {overview?.features && (() => {
                    try {
                      const featuresArray = typeof overview.features === 'string'
                        ? JSON.parse(overview.features)
                        : overview.features;
                      return featuresArray.length > 0 ? (
                        featuresArray.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                            <span dangerouslySetInnerHTML={{ __html: feature }} />
                          </li>
                        ))
                      ) : null;
                    } catch (e) {
                      console.error('Error parsing features:', e);
                      return null;
                    }
                  })() || (
                      <>
                        <li className="flex items-start space-x-3">
                          <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                          <span><strong>Additional funding up to ₹25 lakhs</strong> – Based on repayment track and property value.</span>
                        </li>

                        <li className="flex items-start space-x-3">
                          <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                          <span><strong>Tenure up to 15 years</strong> – Flexible EMIs to suit your needs.</span>
                        </li>

                        <li className="flex items-start space-x-3">
                          <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                          <span><strong>Lower rates than personal loans</strong> – Affordable and convenient funding.</span>
                        </li>

                        <li className="flex items-start space-x-3">
                          <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                          <span><strong>Minimal documentation</strong> – Quick processing for eligible customers.</span>
                        </li>
                      </>
                    )}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || "img/loan-overview/Composite-Loan-Overview.webp"}
                alt="Aham Housing Finance expert explaining attractive top-up loan interest rates and tax benefits to a customer"
                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>

          </div>
        </div>
      </section>






      {/* Key Benefits Section */}
      <section id="benefits" className="relative py-24 overflow-hidden">

        {/* 🔥 Background – gradient + subtle pattern */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white"></div>

        <div className="relative container mx-auto px-4">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6 leading-tight">
              {benefitsSettings?.section_title || 'Key Benefits & Features'}
            </h2>
            <p className="text-lg md:text-xl text-white mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings?.section_subtitle || 'Discover why our top-up loan is the right choice for you'}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {(keyBenefits.length > 0 ? keyBenefits : benefits).map((benefit, index) => {
              const IconComponent = benefit.icon_name ? iconMap[benefit.icon_name] : benefit.icon;
              return (
                <motion.div
                  key={benefit.id || index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -12, scale: 1.03 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
                >
                  {/* Floating Glow */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-500 rounded-full blur-3xl opacity-20"></div>

                  {/* Icon */}
                  <div className="bg-gradient-to-br from-primary-600 to-primary-400 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    {IconComponent && <IconComponent className="h-9 w-9 text-white" />}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed font-medium">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Button */}
          <div className="mt-16 flex justify-center">
            <a href="/contact" className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                Request a Quote
              </motion.button>
            </a>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Soft Background Blobs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight tracking-tight"
            >
              {eligibilitySettings?.section_title || 'Who Can Apply?'}
            </motion.h2>

            <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {eligibilitySettings?.section_subtitle || "Check your eligibility for an Aham top-up loan"}
            </p>
          </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {eligibilityLoading ? (
              // Loading skeleton
              [1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/70 rounded-2xl p-6 h-24 animate-pulse"></div>
              ))
            ) : (
              (eligibilityCriteria.length > 0 ? eligibilityCriteria : eligibility).map((item, index) => {
                // Get icon component for dynamic data
                let IconComponent = null;
                let gradientFrom = "from-blue-500";
                let gradientTo = "to-cyan-400";

                if (item.icon_name) {
                  // Dynamic data from API
                  IconComponent = eligibilityIconMap[item.icon_name];
                  gradientFrom = `from-${item.gradient_from || 'blue-500'}`;
                  gradientTo = `to-${item.gradient_to || 'cyan-400'}`;
                } else {
                  // Static fallback data
                  IconComponent = icons[index]?.icon;
                  gradientFrom = icons[index]?.from || "from-blue-500";
                  gradientTo = icons[index]?.to || "to-cyan-400";
                }

                return (
                  <motion.div
                    key={item.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="
                    group backdrop-blur-xl bg-white/70 
                    border border-white/40 shadow-xl rounded-2xl 
                    p-6 flex flex-col items-center text-center gap-5 
                    hover:-translate-y-2 hover:shadow-2xl 
                    transition-all duration-300
                  "
                  >
                    {/* Gradient Icon */}
                    <div
                      className={`
                      w-14 h-14 rounded-xl flex items-center justify-center 
                      bg-gradient-to-br ${gradientFrom} ${gradientTo}
                      text-white shadow-lg group-hover:scale-105 transition
                    `}
                    >
                      {IconComponent && <IconComponent className="h-6 w-6" />}
                    </div>

                    <div className="flex-1">
                      <p className="text-gray-800 font-semibold text-lg leading-snug">
                        {item.criteria || item}
                      </p>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section>

      {/* Documents Required Section */}
      <section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Background blur circles */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings?.section_title || 'Documents Required'}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings?.section_subtitle || 'Keep these documents ready for a smooth application process'}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              {documentsLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white/50 rounded-xl p-5 h-20 animate-pulse"></div>
                  ))}
                </div>
              ) : (
                <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(() => {
                    const activeDocuments = documentsData.length > 0
                      ? documentsData.filter(doc => doc.category === activeTab)
                      : documents[activeTab] || [];

                    return activeDocuments.map((doc, index) => (
                      <motion.li
                        key={doc.id || index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.4 }}
                        className="flex items-start gap-4 p-5 bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200"
                      >
                        <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                          <FileText className="h-6 w-6" />
                        </div>
                        <span className="text-gray-900 font-semibold">{doc.document_name || doc}</span>
                      </motion.li>
                    ));
                  })()}
                </motion.ul>
              )}
            </motion.div>

          </div>
        </div>
      </section>


      {/* Interest Rates Section */}
      <section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-4 leading-tight drop-shadow-xl">
              {interestRatesSettings?.section_title || 'Interest Rates'}
            </h2>
            <span className="block text-primary-200 text-2xl mb-8">
              {interestRatesSettings?.section_subtitle || 'Competitive rates tailored to your needs'}
            </span>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-10">
                {interestRatesSettings?.intro_text || 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make top-up loans affordable and stress-free.'}
              </p>

              {/* Interest Rate Cards - Dynamic */}
              {interestRatesLoading ? (
                <div className="grid md:grid-cols-3 gap-8 my-10">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white/10 border border-white/20 rounded-2xl p-6 h-48 animate-pulse"></div>
                  ))}
                </div>
              ) : interestRatesCards.length > 0 ? (
                 <div className="grid md:grid-cols-3 gap-8 my-10">
                  {interestRatesCards.map((card, index) => (
                    <motion.div
                      key={card.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.2 }}
                      viewport={{ once: true }}
                      className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg hover:bg-white/15 transition-all flex flex-col items-center text-center"
                    >
                      <div className="text-3xl mb-3">{card.icon || '💰'}</div>
                      <h4 className="text-xl font-semibold mb-2">{card.title}</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {card.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                 <div className="grid md:grid-cols-3 gap-8 my-10">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">🏠</div>
                    <h4 className="text-xl font-semibold mb-2">Flexible Rates</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      Rates tailored based on loan amount, tenure, credit profile & location.
                    </p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">🔍</div>
                    <h4 className="text-xl font-semibold mb-2">100% Transparency</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      No hidden fees. No surprise charges. Complete clarity from start to finish.
                    </p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9 }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">💛</div>
                    <h4 className="text-xl font-semibold mb-2">Dedicated Support</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      Our expert team guides you at every stage of your construction journey.
                    </p>
                  </motion.div>
                </div>
              )}

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestRatesSettings?.closing_text || 'Begin your top-up loan journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs.'}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestRatesSettings?.cta_link || 'https://aham.mbwhost.in/emi-calculator'}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl inline-block"
              >
                {interestRatesSettings?.cta_text || 'Calculate EMI'}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqsSettings?.section_title || 'Frequently Asked Questions'}
            </h2>
            <p className="text-xl text-gray-600">
              {faqsSettings?.section_subtitle || 'Find answers to common questions about Top-up Loan'}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {faqsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-gray-50 rounded-xl h-20 animate-pulse"></div>
                ))}
              </div>
            ) : faqsData.length > 0 ? (
              faqsData.map((faq, index) => (
                <motion.div
                  key={faq.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="mb-4"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                      ? 'bg-primary-50 border-2 border-primary-600'
                      : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      {activeFAQ === index ? (
                        <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    {activeFAQ === index && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 text-gray-700 leading-relaxed"
                      >
                        {faq.answer}
                      </motion.p>
                    )}
                  </button>
                </motion.div>
              ))
            ) : (
              faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="mb-4"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                      ? 'bg-primary-50 border-2 border-primary-600'
                      : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      {activeFAQ === index ? (
                        <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    {activeFAQ === index && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 text-gray-700 leading-relaxed"
                      >
                        {faq.answer}
                      </motion.p>
                    )}
                  </button>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBannerSettings?.title || 'Ready to Unlock Extra Funds?'}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBannerSettings?.description || 'Take the first step towards fulfilling your financial needs with Aham\'s hassle-free Top-up Loan process'}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaBannerSettings?.button_link || 'https://aham.mbwhost.in/contact'}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {ctaBannerSettings?.button_text || 'Talk to Our Loan Expert'}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default TopUpLoan;